/**
 * DayCloseDialog — counts the cash drawer and closes the active billing
 * session. Surfaces variance vs the engine-computed expected cash so the
 * cashier sees over/short instantly.
 */

import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useBillingSession, type SessionSummary } from '@/billing/useBillingSession';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';

interface Props {
  onClose: () => void;
  onClosed?: () => void;
}

/** Indian currency note/coin denominations, high → low. */
const DENOMS = [500, 200, 100, 50, 20, 10, 5, 2, 1];

export function DayCloseDialog({ onClose, onClosed }: Props) {
  const { activeSession, closeSession, getSessionSummary } = useBillingSession();
  const [summary, setSummary] = useState<SessionSummary | null>(null);
  const [actualCash, setActualCash] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));
  const [showDenoms, setShowDenoms] = useState(false);
  const [counts, setCounts] = useState<Record<number, string>>({});

  // Counting by note drives the counted-cash total.
  function setCount(denom: number, raw: string) {
    const next = { ...counts, [denom]: raw.replace(/[^0-9]/g, '') };
    setCounts(next);
    const total = DENOMS.reduce((sum, d) => sum + d * (parseInt(next[d] || '0', 10) || 0), 0);
    setActualCash(total.toFixed(2));
  }

  useEffect(() => {
    let cancelled = false;
    getSessionSummary().then((s) => {
      if (!cancelled) {
        setSummary(s);
        if (s) setActualCash(s.expectedCash.toFixed(2));
      }
    });
    return () => { cancelled = true; };
  }, [getSessionSummary]);

  async function handleClose() {
    const amt = parseFloat(actualCash);
    if (!Number.isFinite(amt) || amt < 0) {
      setError('Enter a valid count');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await closeSession(amt);
      notifySuccess('Day closed.');
      onClosed?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not close the day');
    } finally {
      setBusy(false);
    }
  }

  if (!activeSession) {
    return (
      <div className="space-y-3 px-1 py-1">
        <p className="text-body text-fg-secondary">No active session to close.</p>
        <div className="flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  const counted = parseFloat(actualCash) || 0;
  const variance = summary ? counted - summary.expectedCash : 0;

  return (
    <div className="space-y-4 px-1 py-1">
      {summary ? (
        <div className="space-y-1.5">
          <Row label="Opening cash" value={formatCurrency(summary.openingCash)} muted />
          <Row label="Cash sales" value={formatCurrency(summary.cashSales)} muted />
          {summary.cashExpenses > 0 && (
            <Row label="Cash expenses" value={`− ${formatCurrency(summary.cashExpenses)}`} muted />
          )}
          <div className="flex items-baseline justify-between border-t border-stroke-hairline/10 pt-2">
            <span className="text-heading text-fg-primary">Expected</span>
            <span className="text-heading text-fg-primary tabular-nums">
              {formatCurrency(summary.expectedCash)}
            </span>
          </div>
        </div>
      ) : (
        <div className="text-body text-fg-tertiary">Computing summary…</div>
      )}

      <div>
        <label className="text-label uppercase text-fg-tertiary">Counted cash in drawer</label>
        <Input
          type="number"
          min={0}
          step="0.01"
          value={actualCash}
          onChange={(e) => setActualCash(e.target.value)}
          className="mt-1 text-right tabular-nums"
        />
        {summary && actualCash !== '' && (
          <div className={cn(
            'mt-1 text-label uppercase tabular-nums',
            Math.abs(variance) < 0.01 ? 'text-success-ink' : variance > 0 ? 'text-warning-ink' : 'text-danger-ink',
          )}>
            Variance: {variance >= 0 ? '+' : ''}{formatCurrency(variance)}
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowDenoms((v) => !v)}
          className="mt-2 text-label uppercase text-accent-ink hover:underline"
        >
          {showDenoms ? 'Hide note counter' : 'Count by note'}
        </button>

        {showDenoms && (
          <div className="mt-2 rounded-md border border-stroke-hairline/10 bg-bg-canvas p-2.5">
            <div className="grid grid-cols-3 gap-2">
              {DENOMS.map((d) => (
                <label
                  key={d}
                  className="flex items-center gap-1.5 rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1.5"
                >
                  <span className="w-9 shrink-0 text-label tabular-nums text-fg-secondary">₹{d}</span>
                  <input
                    type="number"
                    min={0}
                    inputMode="numeric"
                    value={counts[d] ?? ''}
                    onChange={(e) => setCount(d, e.target.value)}
                    placeholder="0"
                    className="w-full min-w-0 bg-transparent text-right text-body tabular-nums outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                </label>
              ))}
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-stroke-hairline/10 pt-2 text-body">
              <span className="text-fg-secondary">Counted</span>
              <span className="font-medium tabular-nums text-fg-primary">
                {formatCurrency(parseFloat(actualCash) || 0)}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={handleClose} disabled={busy || !summary}>
          {busy ? 'Closing…' : 'Close day'}
        </Button>
      </div>
    </div>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between text-body">
      <span className={muted ? 'text-fg-tertiary' : 'text-fg-secondary'}>{label}</span>
      <span className={cn('tabular-nums', muted ? 'text-fg-secondary' : 'text-fg-primary')}>{value}</span>
    </div>
  );
}
